import React, { FC, useEffect } from 'react';
import { Col, Form, FormInstance, Row } from 'antd';
import hash from 'object-hash';
import { Input, InputNumber, useMaxBreakpoint } from '@cloudcar-app/cloudcar-components';
import getScreenSpacings from 'utils/getScreenSpacings';
import MessageError from 'utils/message.errors';
import rutFormValidator from 'utils/rutFormValidator';
import { useMutation } from '@apollo/client';
import {
  AllowedCurrency,
  AllowedPaymentMethodType,
  CreateLitePurchaseIntentInput,
  LitePurchaseIntent,
  PaymentMethod,
  PaymentMethodInput,
  PurchaseIntentFormValues,
} from 'apollo/types';
import operations from 'apollo/operations';
import { customerRut, listPrice, selectedPaymentMethod } from 'apollo/cache';
import styles from '../PurchaseIntentForm.module.scss';

interface SpecificFormProps {
  createPurchaseIntentLoading: boolean;
  handleChangeRut: (rut: string) => Promise<string>;
  form: FormInstance<unknown>;
}

const TannerForm: FC<SpecificFormProps> = (props: SpecificFormProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    // eslint-disable-next-line
    createPurchaseIntentLoading,
    handleChangeRut,
    form,
  } = props;

  const maxScreen = useMaxBreakpoint();
  const screenSpacings = getScreenSpacings(maxScreen);

  const [createLitePurchaseIntent, { data: purchaseIntentData }] = useMutation<
    { createLitePurchaseIntent: LitePurchaseIntent },
    { createLitePurchaseIntentInput: CreateLitePurchaseIntentInput }
  >(operations.CREATE_LITE_PURCHASE_INTENT);

  // eslint-disable-next-line
  const [createPaymentMethod, { data: paymentMethodData }] = useMutation<
    { createPaymentMethod: PaymentMethod },
    { paymentMethodInput: PaymentMethodInput }
  >(operations.POST_PAYMENT_METHOD);

  async function isFormValid() {
    try {
      await form.validateFields(['year', 'price', 'rut']);
      return true;
    } catch {
      return false;
    }
  }

  const submitPurchaseInfo = async ({
    brand,
    model,
    year,
    version,
    color,
    price,
    cit,
    rut,
    vehicleType,
  }: PurchaseIntentFormValues) => {
    const carMinInfoId = hash({
      brand,
      model,
      year,
      version,
      color,
    });
    const rutFlat = rut?.split('.').join('').split('-').join('');
    const createLitePurchaseIntentInput = {
      vehicleType: vehicleType || '',
      carMinInfoId,
      brand: brand?.split('#')[1] || '',
      model: model?.split('#')[1] || '',
      year,
      version: version?.split('#')[1] || 'No aplica',
      brandId: (form.getFieldValue('brandId') as string | undefined)?.toString() || '3',
      modelId:
        (form.getFieldValue('modelId') as string | undefined)?.toString() || '5391',
      versionId:
        (form.getFieldValue('versionId') as string | undefined)?.toString() || '11765',
      legalEntity: false,
      color: color || '',
      price,
      cit: cit || '',
      customerRut: `${rutFlat.slice(0, -1)}-${rutFlat.slice(-1)}` || '',
    };
    customerRut(rut);
    await createLitePurchaseIntent({
      variables: {
        createLitePurchaseIntentInput,
      },
    });
    listPrice(price);
    selectedPaymentMethod(AllowedPaymentMethodType.WithFinancing);
  };

  const createPaymentMethodFunction = async () => {
    await createPaymentMethod({
      variables: {
        paymentMethodInput: {
          currency: AllowedCurrency.Clp,
          type: AllowedPaymentMethodType.WithFinancing,
        },
      },
    });
  };

  useEffect(() => {
    if (purchaseIntentData && purchaseIntentData.createLitePurchaseIntent) {
      localStorage.setItem(
        'purchaseIntentId',
        purchaseIntentData.createLitePurchaseIntent.purchaseIntentId
      );
      createPaymentMethodFunction();
    }
  }, [purchaseIntentData]);

  const priceFormatter = (valueToFormat: string | number | undefined) =>
    `$ ${valueToFormat || 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  const valueParser = (valueToParse: string | undefined) =>
    (valueToParse || '0').replace(/\$\s?|(\.*)/g, '');

  return (
    <>
      <Row gutter={[screenSpacings.L, screenSpacings.L]}>
        <Col xs={24} sm={8}>
          <Form.Item
            name="year"
            className={styles.FormInput}
            rules={[
              {
                required: true,
                message: MessageError.emptyFieldsMessages.year,
              },
              {
                type: 'number',
                message: MessageError.invalidFormatFieldsMessages.year,
              },
              {
                validator: (_, value) => {
                  const currentYear = new Date().getFullYear();
                  if (!value || (value >= 1900 && value <= currentYear)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(`El año debe estar entre 1900 y ${currentYear}`)
                  );
                },
              },
            ]}
          >
            <InputNumber
              label="Año"
              placeholder="Año del vehículo"
              required
              compact
              onBlur={async () => {
                const isValid = await isFormValid();
                if (isValid) {
                  const submitInfo = (await form.getFieldsValue([
                    'year',
                    'price',
                    'rut',
                  ])) as PurchaseIntentFormValues;
                  await submitPurchaseInfo(submitInfo);
                }
              }}
              className={styles.Input}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            name="price"
            className={styles.FormInput}
            rules={[
              {
                required: true,
                message: MessageError.emptyFieldsMessages.price,
              },
              {
                type: 'number',
                message: MessageError.invalidFormatFieldsMessages.price,
              },
              {
                validator: (_, value) => {
                  if (value >= 0) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('El número debe ser mayor o igual a 0')
                  );
                },
              },
            ]}
          >
            <InputNumber
              formatter={priceFormatter}
              parser={valueParser}
              placeholder="Precio  de venta"
              onBlur={async () => {
                const isValid = await isFormValid();
                if (isValid) {
                  const submitInfo = (await form.getFieldsValue([
                    'year',
                    'price',
                    'rut',
                  ])) as PurchaseIntentFormValues;
                  await submitPurchaseInfo(submitInfo);
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            name="rut"
            className={styles.FormInput}
            rules={[
              { required: true, message: MessageError.emptyFieldsMessages.rut },
              {
                validator: rutFormValidator,
              },
            ]}
          >
            <Input
              label="Rut cliente"
              placeholder="Rut cliente"
              extra="*Escribe tu rut sin puntos ni guión."
              onChange={(e) => handleChangeRut(e.target.value)}
              required
              compact
              onBlur={async () => {
                const isValid = await isFormValid();
                if (isValid) {
                  const submitInfo = (await form.getFieldsValue([
                    'year',
                    'price',
                    'rut',
                  ])) as PurchaseIntentFormValues;
                  await submitPurchaseInfo(submitInfo);
                }
              }}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

export default TannerForm;
